import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import requestSecondAnimationFrame from '../../../common/utils/lib/requestSecondAnimationFrame';
import { getElementMeasurements, shouldShowPlaceholder } from './elementPlaceholderUtils';
import useUpdatedRef from '../../utils/react/useUpdatedRef';
import ElementPlaceholder from './ElementPlaceholder';
import { MNElement } from '../../../common/elements/elementModelTypes';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { useListPlaceholderContext } from './ListPlaceholderContextProvider';

export type RenderElementPlaceholderProps = {
    boardSection?: string;
    elementId: string;
    element: MNElement;
    savedElementsVisibilityMap?: Map<string, boolean>;
    isElementVisible?: boolean;
    savedPlaceholderMeasurements: { [a: string]: ElementMeasurement } | null;
    allowElementPlaceholder?: boolean;
    setShouldPreventMeasure: (shouldPreventMeasure: boolean) => void;
    onElementVisibilityChange?: (elementId: string, isVisible: boolean) => void;
    elementFilterData?: object;
    inTrash?: boolean;
    isSelected: boolean;
};

type ReturnType = {
    onVisibilityChange: (latestVisibility: boolean) => void;
    shouldRenderPlaceholder: boolean;
    placeholderElement: React.ReactNode;
} | null;

const useElementPlaceholderState = (props: RenderElementPlaceholderProps): ReturnType => {
    const { elementId, element, isSelected, elementFilterData, setShouldPreventMeasure } = props;

    const [isVisibleState, setIsVisibleState] = useState(true);
    const isVisibleStateRef = useUpdatedRef(isVisibleState);
    const latestIsVisibleRef = useRef(isVisibleState);
    const isMountedRef = useRef(false);

    const {
        allowElementPlaceholder,
        initialElementsVisibilityMap,
        onElementVisibilityChange,
        savedPlaceholderMeasurementsRef,
        updateDebugStateIfToolEnabled,
        removeFromDebugStateIfToolEnabled,
    } = useListPlaceholderContext() || {};

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            removeFromDebugStateIfToolEnabled?.(elementId);
        };
    }, []);

    if (!allowElementPlaceholder) return null;

    const onVisibilityChange = useCallback((newIsVisible: boolean) => {
        latestIsVisibleRef.current = newIsVisible;

        // When switching to placeholder, we want to prevent measuring BEFORE the render
        if (!newIsVisible) {
            setShouldPreventMeasure(true);
        }

        requestSecondAnimationFrame(() => {
            const visibilityChangedBeforeFrameRan = newIsVisible !== latestIsVisibleRef.current;
            const visibilityAlreadyCorrect = newIsVisible === isVisibleStateRef.current;

            if (!isMountedRef.current || visibilityChangedBeforeFrameRan || visibilityAlreadyCorrect) return;

            setIsVisibleState(newIsVisible);
            onElementVisibilityChange?.(elementId, newIsVisible);

            // When switching to the actual element, we want to prevent measuring until AFTER the render
            if (newIsVisible) {
                requestAnimationFrame(() => {
                    setShouldPreventMeasure(false);
                });
            }
        });
    }, []);

    const measurements = getElementMeasurements(elementId, savedPlaceholderMeasurementsRef);
    const isElementVisible = useMemo(
        () => (isMountedRef.current ? isVisibleState : (initialElementsVisibilityMap?.get(elementId) ?? true)),
        [isVisibleState, initialElementsVisibilityMap],
    );

    const shouldRenderPlaceholder = shouldShowPlaceholder(
        measurements,
        elementId,
        element,
        isElementVisible,
        elementFilterData,
    );

    const placeholderElement =
        shouldRenderPlaceholder && measurements ? (
            <ElementPlaceholder
                element={element}
                placeholderMeasurements={measurements}
                elementId={elementId}
                isSelected={isSelected}
            />
        ) : null;

    updateDebugStateIfToolEnabled?.(elementId, {
        isElementRendered: !shouldRenderPlaceholder,
        initialElementVisibility: initialElementsVisibilityMap?.get(elementId),
        elementVisibility: isElementVisible,
        measurements,
    });

    return {
        onVisibilityChange,
        shouldRenderPlaceholder,
        placeholderElement,
    };
};

export default useElementPlaceholderState;
